import { createContext, useContext, useEffect, useMemo, useState, useTransition } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import ClientMobileHeader from './ClientMobileHeader.jsx';
import HeaderSearch from './HeaderSearch.jsx';
import HeaderCreateMenu from './HeaderCreateMenu.jsx';
import HeaderMessagesButton from './HeaderMessagesButton.jsx';
import HeaderAccountMenu from './HeaderAccountMenu.jsx';
import QuickBooksSendDrawer from './QuickBooksSendDrawer.jsx';
import { PRIMARY_TABS, getActiveNav } from './ClientSidebar.jsx';
import { useAuth } from '../auth/AuthContext.jsx';
import { api, demoStore } from '../lib/api.js';
import { useSaveToast } from './SaveToast.jsx';
import {
  getLocalSettings,
  connectionsFromSettings,
  orgRoleForEmail,
  roleCanOpenNav,
  roleCanWrite,
  settingsPathForRole,
} from '../lib/settingsStore.js';
import logoLockupSrc from '../assets/brand/demandjet-logo-lockup.png';
import {
  applyLocalMatterFlags,
  activeMatters,
  archivedMatters,
  matterIsDeleted,
} from '../lib/bulkDemandActions.js';
import { normalizeInvoiceId } from '../lib/invoiceDemand.js';
import { buildArchiveUsage } from '../lib/archiveStorage.js';
import {
  buildEvidenceVaultUsage,
  collectEvidenceVaultFiles,
  sumEvidenceVaultBytes,
} from '../lib/evidenceVault.js';
import { ensureCreatedHistory } from '../lib/matterHistory.js';
import { applyMatterPatches } from '../lib/localMatterPatches.js';
import { applyDueDateUnpaidToList } from '../lib/dueDateUnpaid.js';
import { buildDebtorRecords, encodeDebtorId } from '../lib/debtorRecords.js';
import { listDebtorProfiles, removeDebtorProfile } from '../lib/debtorProfiles.js';
import { intakeLocationStateFromDebtor } from '../lib/intakePrefill.js';
import { reportLockerUsage } from '../lib/appNotifications.js';
import {
  activeDebtors,
  applyArchivedDebtorFlags,
  archiveDebtors as archiveDebtorRecords,
  archivedDebtors as listArchivedDebtors,
  excludeDeletedDebtors,
  loadArchivedDebtorMap,
  loadDeletedDebtorMap,
  permanentlyDeleteDebtors,
  unarchiveDebtors as unarchiveDebtorRecords,
} from '../lib/debtorArchive.js';
import { ListFooterProvider, useListFooter } from './listFooterContext.jsx';
import { StickyJetFooter } from './ListPagination.jsx';

const ClientDataContext = createContext(null);

export function useClientData() {
  const ctx = useContext(ClientDataContext);
  if (!ctx) throw new Error('useClientData must be used within ClientLayout');
  return ctx;
}

function enrichMatter(matter) {
  return {
    ...matter,
    Debtor_Company: matter.Debtor_Company || matter.Debtor_Name || '',
    Origin: matter.Origin || 'MANUAL',
    Entry_Tag:
      matter.Entry_Tag || (matter.Origin === 'AUTOMATED' ? 'Automated' : 'Manual'),
  };
}

function mergeMatterLists(...lists) {
  const ids = new Set();
  const out = [];
  for (const list of lists) {
    for (const matter of list || []) {
      const id = String(matter?.ROWID || '');
      if (!id || ids.has(id)) continue;
      ids.add(id);
      out.push(enrichMatter(matter));
    }
  }
  return applyLocalMatterFlags(applyMatterPatches(out));
}

export default function ClientLayout() {
  const { user, logout } = useAuth();
  const { flashSave } = useSaveToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [matters, setMatters] = useState([]);
  const [debtorProfiles, setDebtorProfiles] = useState(() =>
    listDebtorProfiles(user?.email)
  );
  const [archivedDebtorMap, setArchivedDebtorMap] = useState(() => loadArchivedDebtorMap());
  const [deletedDebtorMap, setDeletedDebtorMap] = useState(() => loadDeletedDebtorMap());
  const [qbSendOpen, setQbSendOpen] = useState(false);
  const [pending, startTransition] = useTransition();
  const [notice, setNotice] = useState('');

  const localSettings = getLocalSettings(user?.email || '');
  const qbConnected =
    localSettings.accountingProvider === 'QUICKBOOKS' && localSettings.accountingConnected;
  const accountingConnected = connectionsFromSettings(localSettings).some(
    (conn) => conn.status === 'connected'
  );

  useEffect(() => {
    setDebtorProfiles(listDebtorProfiles(user?.email));
  }, [user?.email]);

  function finalizeMatters(list) {
    const { matters: withDueDateStatus } = applyDueDateUnpaidToList(list || []);
    return withDueDateStatus;
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let imported = [];
      try {
        imported = JSON.parse(sessionStorage.getItem('demandjet.qb.imported') || '[]');
      } catch {
        imported = [];
      }

      const demo = demoStore.matters || [];

      try {
        const data = await api.get('/server/client_api/matters');
        if (!cancelled) {
          setMatters(finalizeMatters(mergeMatterLists(imported, data.matters || [], demo)));
        }
      } catch {
        if (!cancelled) {
          setMatters(finalizeMatters(mergeMatterLists(imported, demo)));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  function refresh() {
    startTransition(async () => {
      let imported = [];
      try {
        imported = JSON.parse(sessionStorage.getItem('demandjet.qb.imported') || '[]');
      } catch {
        imported = [];
      }
      const demo = demoStore.matters || [];
      try {
        const data = await api.get('/server/client_api/matters');
        setMatters(finalizeMatters(mergeMatterLists(imported, data.matters || [], demo)));
      } catch {
        setMatters(finalizeMatters(mergeMatterLists(imported, demo)));
      }
    });
  }

  function openMatter(matter, fromPath = location.pathname) {
    if (!matter?.ROWID) return;
    navigate(`/client/demands/${encodeURIComponent(matter.ROWID)}`, {
      state: { from: fromPath },
    });
  }

  function openDebtor(debtor, fromPath = location.pathname) {
    if (!debtor?.id) return;
    navigate(`/client/debtors/${encodeDebtorId(debtor.id)}`, {
      state: { from: fromPath },
    });
  }

  function openIntake(debtor = null, fromPath = location.pathname) {
    if (debtor) {
      navigate('/client/demands/new', {
        state: intakeLocationStateFromDebtor(debtor, { from: fromPath }),
      });
      return;
    }
    navigate('/client/demands/new');
  }

  function openDebtorCreate() {
    navigate('/client/debtors/new');
  }

  function refreshDebtors() {
    setDebtorProfiles(listDebtorProfiles(user?.email));
    setArchivedDebtorMap(loadArchivedDebtorMap());
    setDeletedDebtorMap(loadDeletedDebtorMap());
  }

  function archiveDebtors(rows) {
    const result = archiveDebtorRecords(rows, archivedDebtorMap);
    setArchivedDebtorMap(result.map);
    return result.updated;
  }

  function unarchiveDebtors(rows) {
    const result = unarchiveDebtorRecords(rows, archivedDebtorMap);
    setArchivedDebtorMap(result.map);
    return result.updated;
  }

  function deleteDebtors(rows) {
    for (const debtor of rows || []) {
      const id = String(debtor?.id || '');
      if (!id) continue;
      try {
        removeDebtorProfile(user?.email, id);
      } catch {
        // Profile may already be gone for matter-only contacts.
      }
    }
    const result = permanentlyDeleteDebtors(rows, {
      archivedMap: archivedDebtorMap,
      deletedMap: deletedDebtorMap,
    });
    setArchivedDebtorMap(result.archivedMap);
    setDeletedDebtorMap(result.deletedMap);
    setDebtorProfiles(listDebtorProfiles(user?.email));
    return result.updated;
  }

  function onIntakeSubmitted(result) {
    const matter = enrichMatter(result.matter || result);
    ensureCreatedHistory(matter, {
      actorName: user?.displayName || user?.email || '',
      actorRole: user?.role || 'CLIENT',
    });
    setMatters((prev) => [matter, ...prev]);
    flashSave('Case submitted at Intake.');
    navigate(`/client/demands/${encodeURIComponent(matter.ROWID)}`, {
      state: { from: '/client/demands' },
    });
  }

  function handleQbDemandsSent(sentMatters) {
    if (!sentMatters?.length) return;
    setMatters((prev) => {
      const ids = new Set(prev.map((m) => String(m.ROWID)));
      const external = new Set(
        prev.map((m) => normalizeInvoiceId(m.External_Invoice_ID)).filter(Boolean)
      );
      const incoming = sentMatters
        .filter((m) => {
          if (ids.has(String(m.ROWID))) return false;
          const key = normalizeInvoiceId(m.External_Invoice_ID);
          return !key || !external.has(key);
        })
        .map(enrichMatter);
      incoming.forEach((matter) => {
        ensureCreatedHistory(matter, {
          actorName: user?.displayName || user?.email || '',
          actorRole: user?.role || 'CLIENT',
        });
      });
      return [...incoming, ...prev];
    });
    flashSave(
      `Sent ${sentMatters.length} QuickBooks demand${sentMatters.length === 1 ? '' : 's'}.`
    );
    navigate('/client/demands');
  }

  const visibleMatters = useMemo(() => activeMatters(matters), [matters]);
  const archivedList = useMemo(() => archivedMatters(matters), [matters]);
  const demandMatters = useMemo(
    () => (matters || []).filter((matter) => !matterIsDeleted(matter)),
    [matters]
  );
  const archiveUsage = useMemo(
    () =>
      buildArchiveUsage({
        used: archivedList.length,
        billing: localSettings.billing || {},
      }),
    [archivedList.length, localSettings.billing]
  );
  const vaultUsage = useMemo(() => {
    const files = collectEvidenceVaultFiles(matters);
    return buildEvidenceVaultUsage({
      usedBytes: sumEvidenceVaultBytes(files),
      billing: localSettings.billing || {},
    });
  }, [matters, localSettings.billing]);

  useEffect(() => {
    void reportLockerUsage(vaultUsage);
  }, [vaultUsage.ratio, vaultUsage.usedGb, vaultUsage.gbLimit]);

  const allDebtors = useMemo(
    () =>
      excludeDeletedDebtors(
        applyArchivedDebtorFlags(
          buildDebtorRecords(visibleMatters, debtorProfiles),
          archivedDebtorMap
        ),
        deletedDebtorMap
      ),
    [visibleMatters, debtorProfiles, archivedDebtorMap, deletedDebtorMap]
  );
  const debtors = useMemo(() => activeDebtors(allDebtors), [allDebtors]);
  const archivedDebtors = useMemo(() => listArchivedDebtors(allDebtors), [allDebtors]);

  function openQbSend() {
    setQbSendOpen(true);
  }

  const value = {
    matters: visibleMatters,
    allMatters: demandMatters,
    evidenceMatters: matters,
    archivedMatters: archivedList,
    archiveUsage,
    vaultUsage,
    setMatters,
    debtors,
    allDebtors,
    archivedDebtors,
    notice,
    setNotice,
    pending,
    refresh,
    refreshDebtors,
    archiveDebtors,
    unarchiveDebtors,
    deleteDebtors,
    qbConnected,
    accountingConnected,
    openIntake,
    openDebtorCreate,
    onIntakeSubmitted,
    openQbSend,
    openMatter,
    openDebtor,
  };

  const { active: activeNav, primaryTabs } = getActiveNav(location.pathname, location.search);
  const orgRole = orgRoleForEmail(user?.email);
  const canWrite = roleCanWrite(orgRole);
  const canCreate =
    canWrite &&
    (roleCanOpenNav(orgRole, 'demands') || roleCanOpenNav(orgRole, 'debtors'));
  const settingsPath = settingsPathForRole(orgRole);
  const tabs = (primaryTabs || PRIMARY_TABS).filter((item) => roleCanOpenNav(orgRole, item.id));

  useEffect(() => {
    if (!canWrite && /\/debtors\/new\/?$/.test(location.pathname)) {
      navigate('/client/debtors', { replace: true });
      return;
    }
    if (!canWrite && /\/demands\/new\/?$/.test(location.pathname)) {
      navigate('/client/demands', { replace: true });
      return;
    }
    if (activeNav.id === 'settings') return;
    if (roleCanOpenNav(orgRole, activeNav.id)) return;
    navigate('/client/home', { replace: true });
  }, [activeNav.id, canWrite, location.pathname, navigate, orgRole]);
  const messagesViewer = useMemo(
    () => ({ email: user?.email || '', name: user?.name || '' }),
    [user?.email, user?.name]
  );

  return (
    <ClientDataContext.Provider value={value}>
      <ListFooterProvider>
        <div className="client-app">
          <div className="client-app-main">
            <ClientMobileHeader
              matters={visibleMatters}
              evidenceMatters={matters}
              debtors={debtors}
              navTabs={tabs}
              settingsPath={settingsPath}
              canWrite={canCreate}
              onOpenMatter={openMatter}
              onNewDemand={openIntake}
              onNewDebtor={openDebtorCreate}
              onSignOut={logout}
              user={user}
              messagesViewer={messagesViewer}
            />

            <header className="client-app-topbar">
              <div className="client-app-topbar-brand-wrap">
                <Link to="/client/home" className="client-app-topbar-brand" aria-label="DemandJet home">
                  <img
                    className="client-app-topbar-logo"
                    src={logoLockupSrc}
                    alt="DemandJet"
                    decoding="async"
                  />
                </Link>
              </div>

              <nav className="client-top-nav" aria-label="Primary">
                <ul className="client-top-nav-list">
                  {tabs.map((item) => (
                    <li key={item.id}>
                      <NavLink
                        to={item.to}
                        className={
                          activeNav.id === item.id
                            ? 'client-top-nav-item is-active'
                            : 'client-top-nav-item'
                        }
                      >
                        {item.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </nav>

              <div className="client-app-topbar-actions">
                {canCreate ? (
                  <HeaderCreateMenu onNewDemand={openIntake} onNewDebtor={openDebtorCreate} />
                ) : null}
                <HeaderSearch
                  matters={visibleMatters}
                  evidenceMatters={matters}
                  debtors={debtors}
                  onOpenMatter={openMatter}
                />
                <HeaderMessagesButton matters={visibleMatters} viewer={messagesViewer} />
                <HeaderAccountMenu user={user} onSignOut={logout} settingsPath={settingsPath} />
              </div>
            </header>

            <div className="client-app-content">
              <Outlet />
            </div>
          </div>

          <ClientStickyJetFooter />

          <QuickBooksSendDrawer
            open={qbSendOpen}
            email={user?.email}
            onClose={() => setQbSendOpen(false)}
            onDemandsSent={handleQbDemandsSent}
          />
        </div>
      </ListFooterProvider>
    </ClientDataContext.Provider>
  );
}

function ClientStickyJetFooter() {
  const footer = useListFooter();
  return <StickyJetFooter pagination={footer?.pagination || null} />;
}
